import * as React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { EVENT_NAMES, OPERATION_STATUSES } from './events.types';
import type { EventFilterValues, OperationStatus } from './events.types';

export interface ClientFilterOption {
  label: string;
  value: string;
}

interface EventFiltersDrawerProps {
  clientOptions: ClientFilterOption[];
  clientsLoading: boolean;
  filters: EventFilterValues;
  onApply: () => void;
  onCancel: () => void;
  onClear: () => void;
  onFiltersChange: (filters: EventFilterValues) => void;
  open: boolean;
}

const OPERATION_STATUS_LABELS: Record<OperationStatus, string> = {
  SUCCESS: 'Success',
  FAILED: 'Failed',
  IN_PROGRESS: 'In Progress',
};

interface FilterLabelProps {
  children: React.ReactNode;
  htmlFor: string;
}

const FilterLabel = ({ children, htmlFor }: FilterLabelProps) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    variant="body2"
    sx={{ mb: 0.75, display: 'block' }}
  >
    {children}
  </Typography>
);

const renderSearchInput = (params: AutocompleteRenderInputParams) => (
  <TextField {...params} placeholder="Search" size="small" />
);

export default function EventFiltersDrawer({
  clientOptions,
  clientsLoading,
  filters,
  onApply,
  onCancel,
  onClear,
  onFiltersChange,
  open,
}: EventFiltersDrawerProps) {
  const theme = useTheme();
  const selectedClient = clientOptions.find(
    (option) => option.value === filters.clientName,
  ) ?? null;

  const updateFilter = <Key extends keyof EventFilterValues>(
    key: Key,
    value: EventFilterValues[Key],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onCancel}
      sx={{ zIndex: theme.zIndex.drawer + 2 }}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': 'event-filters-title',
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100vw',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ px: 3, pt: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography id="event-filters-title" component="h2" variant="h6">
              Select Filters
            </Typography>
            <IconButton aria-label="Close event filters" onClick={onCancel} edge="end">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ mt: 2 }} />
        </Box>

        <Stack spacing={2.5} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
          <Box>
            <FilterLabel htmlFor="event-name-filter">Event Name</FilterLabel>
            <Autocomplete
              id="event-name-filter"
              options={EVENT_NAMES}
              value={filters.eventName || null}
              onChange={(_event, value) => updateFilter('eventName', value ?? '')}
              renderInput={renderSearchInput}
            />
          </Box>

          <Box>
            <FilterLabel htmlFor="client-name-filter">Client Name</FilterLabel>
            <Autocomplete
              id="client-name-filter"
              options={clientOptions}
              value={selectedClient}
              onChange={(_event, value) => updateFilter('clientName', value?.value ?? '')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              loading={clientsLoading}
              noOptionsText={clientsLoading ? 'Loading clients...' : 'No clients found'}
              renderInput={renderSearchInput}
            />
          </Box>

          <Box>
            <FilterLabel htmlFor="operation-status-filter">Operation Status</FilterLabel>
            <Autocomplete
              id="operation-status-filter"
              options={OPERATION_STATUSES}
              value={filters.operationStatus || null}
              onChange={(_event, value) => updateFilter('operationStatus', value ?? '')}
              getOptionLabel={(option) => OPERATION_STATUS_LABELS[option]}
              renderInput={renderSearchInput}
            />
          </Box>

          <Box>
            <FilterLabel htmlFor="initiated-by-filter">Initiated By</FilterLabel>
            <TextField
              id="initiated-by-filter"
              fullWidth
              value={filters.initiatedBy}
              onChange={(event) => updateFilter('initiatedBy', event.target.value)}
              placeholder="Search"
              inputProps={{ 'aria-label': 'Filter events by initiated by' }}
              size="small"
            />
          </Box>
        </Stack>

        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            px: 3,
            py: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button color="inherit" onClick={onClear}>
              Clear
            </Button>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="contained" onClick={onApply}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
